import { SlidersHorizontal, X } from "lucide-react";
import { useEffect, useState } from "react";

import type { TAdminUser } from "@/api/admin/admin.types";
import { UserPicker } from "@/components/admin/UserPicker";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover";
import { MultiSelect, Select } from "@/components/ui/Select";

import { type TDateRangeValue, type TFilterDef, type TFilterValue } from "./types";

/**
 * `user` filter control. Admins pick a user by name/email; the filter value
 * sent to the fetcher is the resolved user id (so the backend keeps filtering
 * by user id). Holds the selected user locally for display, and resets it when
 * the grid clears the filter value externally.
 */
function UserFilterControl({
  value,
  placeholder,
  onChange,
}: {
  value: TFilterValue;
  placeholder?: string;
  onChange: (value: TFilterValue) => void;
}) {
  const [selected, setSelected] = useState<TAdminUser | null>(null);

  useEffect(() => {
    // External clear (e.g. "Clear filters") drops the id → drop the display.
    if (!value && selected) setSelected(null);
  }, [value, selected]);

  return (
    <UserPicker
      value={selected}
      placeholder={placeholder ?? "Search by name or email…"}
      onChange={(user) => {
        setSelected(user);
        onChange(user?.id);
      }}
    />
  );
}

const INLINE_LIMIT = 3;

type TDataGridFiltersProps = {
  filters: TFilterDef[];
  values: Record<string, TFilterValue>;
  onChange: (id: string, value: TFilterValue) => void;
  onClear: () => void;
  activeCount: number;
};

function FilterControl({
  filter,
  value,
  onChange,
}: {
  filter: TFilterDef;
  value: TFilterValue;
  onChange: (value: TFilterValue) => void;
}) {
  switch (filter.type) {
    case "text":
      return (
        <Input
          className="h-9"
          value={typeof value === "string" ? value : ""}
          placeholder={filter.placeholder ?? filter.label}
          onChange={(e) => onChange(e.target.value)}
          aria-label={filter.label}
        />
      );
    case "user":
      return (
        <UserFilterControl
          value={value}
          placeholder={filter.placeholder}
          onChange={onChange}
        />
      );
    case "select":
      return (
        <Select
          value={typeof value === "string" ? value : ""}
          options={filter.options ?? []}
          placeholder={filter.placeholder ?? `All ${filter.label.toLowerCase()}`}
          onValueChange={(v) => onChange(v)}
          aria-label={filter.label}
        />
      );
    case "enum":
      return (
        <MultiSelect
          options={filter.options ?? []}
          value={Array.isArray(value) ? value : []}
          onValueChange={(v) => onChange(v)}
          placeholder={filter.placeholder ?? filter.label}
          aria-label={filter.label}
        />
      );
    case "boolean":
      return (
        <Select
          value={value === true ? "true" : value === false ? "false" : ""}
          options={[
            { label: "Yes", value: "true" },
            { label: "No", value: "false" },
          ]}
          placeholder="Any"
          onValueChange={(v) => onChange(v === "" ? undefined : v === "true")}
          aria-label={filter.label}
        />
      );
    case "dateRange": {
      const range: TDateRangeValue = typeof value === "object" && !Array.isArray(value) ? value : {};
      return (
        <div className="flex items-center gap-1">
          <Input
            type="date"
            className="h-9"
            value={range.from ?? ""}
            onChange={(e) => onChange({ ...range, from: e.target.value })}
            aria-label={`${filter.label} from`}
          />
          <span className="text-muted-foreground">–</span>
          <Input
            type="date"
            className="h-9"
            value={range.to ?? ""}
            onChange={(e) => onChange({ ...range, to: e.target.value })}
            aria-label={`${filter.label} to`}
          />
        </div>
      );
    }
    default:
      return null;
  }
}

function FilterField({
  filter,
  value,
  onChange,
}: {
  filter: TFilterDef;
  value: TFilterValue;
  onChange: (value: TFilterValue) => void;
}) {
  return (
    <div
      className={`flex flex-col gap-1 ${
        filter.type === "user" ? "min-w-[16rem]" : "min-w-[10rem]"
      }`}
    >
      <label className="text-xs font-medium text-muted-foreground">{filter.label}</label>
      <FilterControl
        filter={filter}
        value={value}
        onChange={onChange}
      />
    </div>
  );
}

/**
 * Renders configured filters inline; collapses extras beyond the first few
 * into an "Advanced filters" popover. Applying a filter resets pagination
 * (handled by the grid's setFilterValue).
 */
export function DataGridFilters({ filters, values, onChange, onClear, activeCount }: TDataGridFiltersProps) {
  if (filters.length === 0) return null;

  const inline = filters.slice(0, INLINE_LIMIT);
  const advanced = filters.slice(INLINE_LIMIT);

  return (
    <div className="flex flex-wrap items-end gap-3">
      {inline.map((filter) => (
        <FilterField
          key={filter.id}
          filter={filter}
          value={values[filter.id]}
          onChange={(value) => onChange(filter.id, value)}
        />
      ))}

      {advanced.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
            >
              <SlidersHorizontal className="mr-2 h-4 w-4" />
              Advanced filters
              {activeCount > 0 && (
                <span className="ml-2 rounded-full bg-primary px-1.5 text-xs text-primary-foreground">
                  {activeCount}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-80"
            align="start"
          >
            <div className="flex flex-col gap-3">
              {advanced.map((filter) => (
                <FilterField
                  key={filter.id}
                  filter={filter}
                  value={values[filter.id]}
                  onChange={(value) => onChange(filter.id, value)}
                />
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}

      {activeCount > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClear}
        >
          <X className="mr-1 h-4 w-4" />
          Clear filters
        </Button>
      )}
    </div>
  );
}
